import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useSupabase } from "@/auth/SupabaseProvider";

import { apiFetch } from "./client";

/**
 * TanStack Query hooks for the public access-codes admin surface.
 *
 * The list view pages `/access-codes`; the mint mutation pairs with
 * the form on the same page; the revoke mutation invalidates the
 * list query on success so the table re-fetches and shows the
 * `revoked_at` stamp without a manual refresh.
 *
 * The mint response carries only `{ id, expiresAt }` — the plaintext
 * code is delivered to the prospect by email and never returned
 * over the API. The admin UI surfaces this loudly so the operator
 * doesn't expect to see the code after minting.
 *
 * Errors propagate as {@link ApiError} from {@link apiFetch}; the
 * form surface renders them inline rather than via a toast (the
 * `code-delivery email send failed` 502 case carries `{ id, error,
 * expiresAt }`, so the row exists even though the email didn't —
 * the operator needs to act on that specifically).
 */

export interface AccessCodeRow {
  id: string;
  recipientName: string;
  recipientCompany: string;
  recipientEmail: string;
  expiresAt: string;
  usedAt: string | null;
  revokedAt: string | null;
  burnedAt: string | null;
  attemptCount: number;
  createdAt: string;
  createdBy: string;
}

export interface AccessCodesResponse {
  codes: AccessCodeRow[];
}

export interface MintAccessCodeInput {
  recipientName: string;
  recipientCompany: string;
  recipientEmail: string;
}

export interface MintAccessCodeResponse {
  id: string;
  expiresAt: string;
  /** Present on a 502 response when the row was created but the Resend send failed. */
  error?: string;
}

const QUERY_KEY = ["access-codes"] as const;

export function useAccessCodes() {
  const { client } = useSupabase();
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: ({ signal }) =>
      apiFetch<AccessCodesResponse>(client, "/api/admin/access-codes", { signal }),
  });
}

export function useMintAccessCode() {
  const { client } = useSupabase();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: MintAccessCodeInput) =>
      apiFetch<MintAccessCodeResponse>(client, "/api/admin/access-codes", {
        method: "POST",
        body: input,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

export function useRevokeAccessCode() {
  const { client } = useSupabase();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ id: string; revoked: boolean }>(
        client,
        `/api/admin/access-codes/${id}/revoke`,
        { method: "POST" },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}
